<template>
    <div class="flex">
        <div class="flex items-center">
            <activix-select
                :disabled="disabled"
                :filterable="true"
                :multiple="true"
                :options="contactableUsers"
                :same-width="false"
                label-key="text"
                value-key="id"
                :value="normalizedSelectedItemIds"
                ref="alertUsers"
                @input="syncWithGroups"
            >
                <template #trigger="{ selected }">
                    <activix-tooltip
                        :content="disabledTooltip || $t('clientCard.addCommentMentions')"
                        :show-on-mobile="ariaDisabled"
                    >
                        <span class="inline-flex">
                            <activix-button
                                :aria-describedby="ariaDescribedby || undefined"
                                :aria-disabled="ariaDisabled"
                                :disabled="disabled"
                            >
                                <icon
                                    :class="{ 'text-blue-500': !!selected.length }"
                                    name="regular/single-neutral"
                                />
                                <span class="pl-1 text-xs" v-if="selected.length > 0">{{
                                    selected.length
                                }}</span>
                            </activix-button>
                        </span>
                    </activix-tooltip>
                </template>
            </activix-select>
        </div>
    </div>
</template>

<script>
    import { ActivixSelect, ActivixButton } from '@/components/activix-ui/index.ts';
    import { uniqBy } from 'lodash-es';

    import Account from '@/entities/Account.js';

    const toItemIds = (items) => items.map((item) => item?.id ?? item);
    const hasSameItemIds = (first = [], second = []) =>
        first.every((item) => second.includes(item)) &&
        second.every((item) => first.includes(item));

    export default {
        compatConfig: { WATCH_ARRAY: false },

        components: {
            ActivixButton,
            ActivixSelect,
        },

        emits: ['update:selected-item-ids'],

        props: {
            account: {
                type: Account,
                required: true,
            },
            ariaDescribedby: {
                type: String,
                default: '',
            },
            ariaDisabled: {
                type: Boolean,
                default: false,
            },
            disabledTooltip: {
                type: String,
                default: '',
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            selectedItemIds: {
                type: Array,
                required: true,
            },
            users: {
                type: Array,
                required: true,
            },
        },

        computed: {
            normalizedSelectedItemIds() {
                return toItemIds(this.selectedItemIds);
            },

            activeOrNotifiedUsers() {
                return this.users
                    .filter(
                        (user) =>
                            user.isSelectable || this.normalizedSelectedItemIds.includes(user.id)
                    )
                    .map((user) => ({
                        id: user.id,
                        text: user.fullName,
                        post_id: user.post_id,
                        role_id: user.role_id,
                    }));
            },

            activeUserOptions() {
                return this.users
                    .filter((user) => user.isSelectable)
                    .map((user) => ({
                        id: user.id,
                        text: user.fullName,
                    }));
            },

            contactableUsers() {
                const guestGroups = this.account.guest_groups.map((group) => {
                    return {
                        id: `G${group.id}`,
                        isGroup: true,
                        text: `[${group.name}]`,
                        posts: group.posts,
                        roles: group.roles,
                        users: group.users,
                    };
                });

                return guestGroups.concat(this.activeUserOptions);
            },
        },

        watch: {
            normalizedSelectedItemIds: {
                immediate: true,
                async handler(newValue, oldValue) {
                    if (hasSameItemIds(newValue, oldValue)) {
                        return;
                    }

                    this.syncWithGroups(newValue);

                    await this.$nextTick();

                    if (this.$refs.alertUsers?.search) {
                        this.$refs.alertUsers.search = '';
                        this.$refs.alertUsers.$refs.search?.focus();
                    }
                },
            },
        },

        methods: {
            getActiveGroupIdsByUsers(userIds) {
                return this.account.guest_groups
                    .filter(
                        (group) =>
                            !this.getGroupUserIds(group).some(
                                (groupUserId) => !userIds.includes(groupUserId)
                            )
                    )
                    .map((group) => `G${group.id}`);
            },

            getActiveUsersByGuestGroups(users) {
                return this.activeOrNotifiedUsers.filter((user) => users.includes(user.id));
            },

            getActiveUsersBySelectedPosts(posts) {
                return this.activeOrNotifiedUsers.filter((user) => posts.includes(user.post_id));
            },

            getActiveUsersBySelectedRoles(roles) {
                return this.activeOrNotifiedUsers.filter((user) => roles.includes(user.role_id));
            },

            getAddedGroupUserIds(itemIds) {
                const addedGroupId = itemIds.find(
                    (itemId) =>
                        String(itemId).startsWith('G') &&
                        !this.normalizedSelectedItemIds.includes(itemId)
                );

                if (addedGroupId) {
                    const addedGroup = this.contactableUsers.find(
                        (item) => item.id === addedGroupId
                    );

                    if (addedGroup) {
                        return this.getGroupUserIds(addedGroup);
                    }
                }

                return [];
            },

            getGroupUserIds(group) {
                const groupUsersByPosts = this.getActiveUsersBySelectedPosts(group.posts);
                const groupUsersByRoles = this.getActiveUsersBySelectedRoles(group.roles);
                const groupUsersByUsers = this.getActiveUsersByGuestGroups(group.users);

                return uniqBy(
                    groupUsersByPosts.concat(groupUsersByRoles).concat(groupUsersByUsers),
                    'id'
                ).map((user) => user.id);
            },

            getRemovedGroupUserIds(itemIds) {
                const removedGroupId = this.normalizedSelectedItemIds.find(
                    (itemId) => String(itemId).startsWith('G') && !itemIds.includes(itemId)
                );

                if (removedGroupId) {
                    const removedGroup = this.contactableUsers.find(
                        (item) => item.id === removedGroupId
                    );

                    if (removedGroup) {
                        return this.getGroupUserIds(removedGroup);
                    }
                }

                return [];
            },

            syncWithGroups(itemIds) {
                const normalizedItemIds = toItemIds(itemIds);
                const syncedItemIds = new Set(normalizedItemIds);

                this.getAddedGroupUserIds(normalizedItemIds).forEach(
                    syncedItemIds.add,
                    syncedItemIds
                );
                this.getRemovedGroupUserIds(normalizedItemIds).forEach(
                    syncedItemIds.delete,
                    syncedItemIds
                );

                const activeGroupIds = this.getActiveGroupIdsByUsers([...syncedItemIds]);
                activeGroupIds.forEach(syncedItemIds.add, syncedItemIds);

                const inactiveGroupIds = [...syncedItemIds].filter(
                    (itemId) => String(itemId).startsWith('G') && !activeGroupIds.includes(itemId)
                );
                inactiveGroupIds.forEach(syncedItemIds.delete, syncedItemIds);

                const selection = [...syncedItemIds];

                if (!hasSameItemIds(selection, this.selectedItemIds)) {
                    this.$emit('update:selected-item-ids', selection);
                }
            },
        },
    };
</script>
